import { useState, useRef, useEffect } from "react";
import { Globe, ExternalLink, RefreshCw, Camera, MousePointer2, Pencil, Maximize2, Minimize2, PanelRightClose, PanelRightOpen, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * WebsiteCanvas — markup.io-class website review surface.
 *
 * Two-mode design:
 *   "browse"   = LIVE iframe of the URL. Reviewer navigates and scrolls naturally.
 *                Annotations are NOT possible in this mode (cross-origin iframes can't be measured).
 *   "annotate" = STATIC full-page screenshot of the current URL, rendered as a scrollable <img>.
 *                Pins/areas anchor to the image, scroll naturally with content, can be precisely targeted.
 *
 * Each annotation stores { type, x, y, w?, h?, url, scroll_y }. Pins move with the page because
 * the scroll container IS the canvas, not the iframe. When the user enters Annotate mode for a URL
 * for the first time, the canvas auto-captures a full-page screenshot via Playwright (cached per
 * (version_id, url) so subsequent annotations on the same page reuse it instantly).
 */
export default function WebsiteCanvas({
  url,
  comments,
  readOnly = false,
  activeTool = "pin",
  onCreateAnnotation,
  selectedCommentId,
  setSelectedCommentId,
  pendingAnno = null,
  testidPrefix = "anno",
  reviewItemId = null,
  versionId = null,           // auth path: POST /versions/{vid}/page-screenshot
  publicToken = null,         // public path: POST /r/{token}/page-screenshot
  fullscreen = false,
  onToggleFullscreen = null,
}) {
  const [currentUrl, setCurrentUrl] = useState(url || "");
  const [iframeKey, setIframeKey] = useState(0);
  const [mode, setMode] = useState("browse");
  const [history, setHistory] = useState([url || ""]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const iframeRef = useRef(null);
  const imgWrapRef = useRef(null);
  const [rectStart, setRectStart] = useState(null);
  const [dragRect, setDragRect] = useState(null);

  // Per-URL screenshot cache for annotate mode. Map<url, dataUrl>.
  const [pageScreenshots, setPageScreenshots] = useState({});
  const [capturingUrl, setCapturingUrl] = useState(null);
  // The URL we're CURRENTLY annotating (locked when we entered annotate mode for that page).
  // This prevents pins from being mis-tagged with a stale `currentUrl` if the user later types a different URL.
  const [annotatedUrl, setAnnotatedUrl] = useState(null);

  // If the file_data_url itself IS already an image, we treat it as a single-page screenshot
  // (image-type review item served as data:image/* URL). Pins anchor naturally to the image.
  const isInlineImage = typeof url === "string" && url.startsWith("data:image/");
  const effectiveAnnotatedUrl = isInlineImage ? (url || "") : (annotatedUrl || currentUrl);
  const annotateImageSrc = isInlineImage ? url : pageScreenshots[effectiveAnnotatedUrl];
  const inAnnotateMode = mode === "annotate" || isInlineImage;

  // ----- Helpers -----

  const captureScreenshotForUrl = async (targetUrl, force = false) => {
    if (!targetUrl) return null;
    if (!force && pageScreenshots[targetUrl]) return pageScreenshots[targetUrl];
    setCapturingUrl(targetUrl);
    try {
      let resp;
      const path = force ? "?force=1" : "";
      if (publicToken) {
        resp = await api.post(`/r/${publicToken}/page-screenshot${path}`, { url: targetUrl });
      } else if (versionId) {
        resp = await api.post(`/versions/${versionId}/page-screenshot${path}`, { url: targetUrl });
      } else {
        throw new Error("Annotation requires either versionId or publicToken");
      }
      const dataUrl = resp.data?.data_url;
      if (!dataUrl) throw new Error("Empty screenshot response");
      setPageScreenshots((m) => ({ ...m, [targetUrl]: dataUrl }));
      return dataUrl;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't capture page screenshot"));
      return null;
    } finally {
      setCapturingUrl(null);
    }
  };

  // Switch to annotate mode: ensure screenshot for currentUrl is cached, lock annotatedUrl.
  const enterAnnotateMode = async () => {
    if (!currentUrl) return toast.error("Navigate to a URL first");
    setMode("annotate");
    setAnnotatedUrl(currentUrl);
    await captureScreenshotForUrl(currentUrl);
  };

  // Re-capture the screenshot for the page we're currently annotating (cache-bust).
  const recaptureScreenshot = async () => {
    const u = effectiveAnnotatedUrl;
    if (!u) return;
    toast.info("Recapturing…");
    await captureScreenshotForUrl(u, true);
    toast.success("Screenshot refreshed");
  };

  const enterBrowseMode = () => {
    setMode("browse");
    setRectStart(null);
    setDragRect(null);
  };

  // Scroll the annotate-mode wrap to a specific y.
  const scrollImgWrapTo = (y) => {
    if (!imgWrapRef.current) return;
    imgWrapRef.current.scrollTo({ top: y || 0, behavior: "smooth" });
  };

  // ----- URL bar / navigation (iframe browse mode) -----

  const pushHistory = (newUrl) => {
    setHistory((h) => [...h.slice(0, historyIdx + 1), newUrl]);
    setHistoryIdx((i) => i + 1);
  };

  const goToUrl = async (newUrl) => {
    const trimmed = (newUrl || "").trim();
    if (!trimmed) return;
    const normalized = /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
    setCurrentUrl(normalized);
    setIframeKey((k) => k + 1);
    pushHistory(normalized);
    // KEY UX FIX: when user types a new URL in annotate mode, they want to annotate
    // *that* page — not stay locked on the previously-captured screenshot. Re-anchor
    // annotatedUrl and capture the screenshot for the new page in the background.
    if (mode === "annotate" && !isInlineImage) {
      setAnnotatedUrl(normalized);
      await captureScreenshotForUrl(normalized);
    }
  };

  const goBack = async () => {
    if (historyIdx <= 0) return;
    const dest = history[historyIdx - 1];
    setHistoryIdx((i) => i - 1);
    setCurrentUrl(dest);
    setIframeKey((k) => k + 1);
    if (mode === "annotate" && !isInlineImage) {
      setAnnotatedUrl(dest);
      await captureScreenshotForUrl(dest);
    }
  };
  const goFwd = async () => {
    if (historyIdx >= history.length - 1) return;
    const dest = history[historyIdx + 1];
    setHistoryIdx((i) => i + 1);
    setCurrentUrl(dest);
    setIframeKey((k) => k + 1);
    if (mode === "annotate" && !isInlineImage) {
      setAnnotatedUrl(dest);
      await captureScreenshotForUrl(dest);
    }
  };

  const handleIframeLoad = () => {
    // Best-effort same-origin URL sync. Cross-origin will throw — silently ignore.
    try {
      const href = iframeRef.current?.contentWindow?.location?.href;
      if (href && href !== "about:blank" && href !== currentUrl) {
        setCurrentUrl(href);
        setHistory((h) => [...h.slice(0, historyIdx + 1), href]);
        setHistoryIdx((i) => i + 1);
      }
    } catch { /* cross-origin */ }
  };

  // Keep currentUrl in sync if the canonical `url` prop changes async.
  useEffect(() => {
    if (url && !currentUrl) {
      setCurrentUrl(url);
      setHistory([url]);
      setHistoryIdx(0);
    }
  }, [url]);

  // Inline-image items go straight to "annotate" since there's no live iframe.
  useEffect(() => {
    if (isInlineImage) setMode("annotate");
  }, [isInlineImage]);

  // When a comment is selected from the panel, navigate to its URL, switch to annotate,
  // load its screenshot, and scroll to its captured scroll_y.
  useEffect(() => {
    if (!selectedCommentId) return;
    const c = comments.find((x) => x.id === selectedCommentId);
    if (!c?.annotation) return;
    const targetUrl = c.annotation.url;
    const targetScrollY = c.annotation.scroll_y || 0;
    const run = async () => {
      if (targetUrl && targetUrl !== currentUrl) {
        setCurrentUrl(targetUrl);
        setHistory((h) => [...h.slice(0, historyIdx + 1), targetUrl]);
        setHistoryIdx((i) => i + 1);
      }
      if (!isInlineImage) {
        setMode("annotate");
        await captureScreenshotForUrl(targetUrl || currentUrl);
      }
      setTimeout(() => scrollImgWrapTo(targetScrollY), 100);
    };
    run();
  }, [selectedCommentId]);

  // ----- Annotation event handlers (operate on the overlay's own rect) -----

  const overlayActive = inAnnotateMode && !readOnly && annotateImageSrc;
  const isDragTool = ["rect", "arrow", "circle"].includes(activeTool);

  const handleOverlayClick = (e) => {
    if (!overlayActive || activeTool !== "pin") return;
    if (rectStart || dragRect) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;
    const scrollY = imgWrapRef.current?.scrollTop || 0;
    onCreateAnnotation && onCreateAnnotation({ type: "pin", x, y, url: effectiveAnnotatedUrl, scroll_y: scrollY });
  };
  const handleMouseDown = (e) => {
    if (!overlayActive || !["rect", "arrow", "circle"].includes(activeTool)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setRectStart({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };
  const handleMouseMove = (e) => {
    if (!rectStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (activeTool === "arrow") {
      setDragRect({ type: "arrow", x: rectStart.x, y: rectStart.y, end_x: x, end_y: y });
    } else if (activeTool === "circle") {
      setDragRect({
        type: "circle",
        x: Math.min(rectStart.x, x),
        y: Math.min(rectStart.y, y),
        w: Math.abs(x - rectStart.x),
        h: Math.abs(y - rectStart.y),
      });
    } else {
      setDragRect({
        type: "rect",
        x: Math.min(rectStart.x, x),
        y: Math.min(rectStart.y, y),
        w: Math.abs(x - rectStart.x),
        h: Math.abs(y - rectStart.y),
      });
    }
  };
  const handleMouseUp = () => {
    if (rectStart && dragRect) {
      const big = dragRect.type === "arrow"
        ? (Math.abs((dragRect.end_x || 0) - dragRect.x) > 1 || Math.abs((dragRect.end_y || 0) - dragRect.y) > 1)
        : ((dragRect.w || 0) > 1 || (dragRect.h || 0) > 1);
      if (big) {
        const scrollY = imgWrapRef.current?.scrollTop || 0;
        onCreateAnnotation && onCreateAnnotation({ ...dragRect, url: effectiveAnnotatedUrl, scroll_y: scrollY });
      }
    }
    setDragRect(null);
    setRectStart(null);
  };

  // Pins/areas visible in the annotate canvas — strictly those whose URL matches the current page.
  // Comments from other pages remain in the side panel under their own URL group.
  const visibleComments = comments.filter((c) => {
    if (!c.annotation) return false;
    const cu = c.annotation.url;
    if (isInlineImage) return true;
    return !cu || cu === effectiveAnnotatedUrl;
  });

  const renderPinsAndRects = () => (
    <>
      {/* SVG layer for arrows + circles (full-canvas overlay, pointer-events: none on container) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100" data-testid={`${testidPrefix}-svg-layer`}>
        <defs>
          <marker id={`arrowhead-${testidPrefix}`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L0,6 L6,3 z" fill="hsl(var(--primary))" />
          </marker>
        </defs>
        {visibleComments.filter(c => c.annotation.type === "arrow").map((c) => {
          const a = c.annotation;
          const isSel = selectedCommentId === c.id;
          return (
            <g key={c.id} style={{ pointerEvents: "auto", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }} data-testid={`${testidPrefix}-arrow-${c.id}`}>
              <line x1={a.x} y1={a.y} x2={a.end_x || a.x} y2={a.end_y || a.y}
                stroke={isSel ? "#fff" : "hsl(var(--primary))"} strokeWidth={isSel ? 0.6 : 0.45}
                markerEnd={`url(#arrowhead-${testidPrefix})`} vectorEffect="non-scaling-stroke" />
            </g>
          );
        })}
        {visibleComments.filter(c => c.annotation.type === "circle").map((c) => {
          const a = c.annotation;
          const isSel = selectedCommentId === c.id;
          const cx = a.x + (a.w || 0) / 2;
          const cy = a.y + (a.h || 0) / 2;
          const rx = (a.w || 0) / 2;
          const ry = (a.h || 0) / 2;
          return (
            <ellipse key={c.id}
              cx={cx} cy={cy} rx={rx} ry={ry}
              fill={isSel ? "hsl(var(--primary) / 0.25)" : "hsl(var(--primary) / 0.10)"}
              stroke={isSel ? "#fff" : "hsl(var(--primary))"} strokeWidth={isSel ? 0.6 : 0.45}
              vectorEffect="non-scaling-stroke"
              style={{ pointerEvents: "auto", cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }}
              data-testid={`${testidPrefix}-circle-${c.id}`} />
          );
        })}
        {dragRect?.type === "arrow" && (
          <line x1={dragRect.x} y1={dragRect.y} x2={dragRect.end_x || dragRect.x} y2={dragRect.end_y || dragRect.y}
            stroke="hsl(var(--primary))" strokeDasharray="1,1" strokeWidth="0.45" markerEnd={`url(#arrowhead-${testidPrefix})`} vectorEffect="non-scaling-stroke" />
        )}
        {dragRect?.type === "circle" && (
          <ellipse cx={dragRect.x + (dragRect.w || 0) / 2} cy={dragRect.y + (dragRect.h || 0) / 2}
            rx={(dragRect.w || 0) / 2} ry={(dragRect.h || 0) / 2}
            fill="hsl(var(--primary) / 0.10)" stroke="hsl(var(--primary))" strokeDasharray="1,1" strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
        )}
        {pendingAnno?.type === "arrow" && (
          <line x1={pendingAnno.x} y1={pendingAnno.y} x2={pendingAnno.end_x || pendingAnno.x} y2={pendingAnno.end_y || pendingAnno.y}
            stroke="#22c55e" strokeWidth="0.5" markerEnd={`url(#arrowhead-${testidPrefix})`} vectorEffect="non-scaling-stroke" />
        )}
        {pendingAnno?.type === "circle" && (
          <ellipse cx={pendingAnno.x + (pendingAnno.w || 0) / 2} cy={pendingAnno.y + (pendingAnno.h || 0) / 2}
            rx={(pendingAnno.w || 0) / 2} ry={(pendingAnno.h || 0) / 2}
            fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        )}
      </svg>

      {visibleComments.filter(c => c.annotation.type === "rect").map((c) => {
        const isSel = selectedCommentId === c.id;
        return (
          <div
            key={c.id}
            className="absolute border-2 cursor-pointer"
            style={{
              left: `${c.annotation.x}%`, top: `${c.annotation.y}%`,
              width: `${c.annotation.w}%`, height: `${c.annotation.h}%`,
              borderColor: isSel ? "#fff" : "hsl(var(--primary))",
              background: isSel ? "hsl(var(--primary) / 0.25)" : "hsl(var(--primary) / 0.10)",
              boxShadow: isSel ? "0 0 0 2px hsl(var(--primary))" : undefined,
              pointerEvents: "auto",
            }}
            onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }}
            data-testid={`${testidPrefix}-rect-${c.id}`}
          />
        );
      })}
      {visibleComments.filter(c => c.annotation.type === "pin" || !c.annotation.type).map((c) => {
        const num = comments.indexOf(c) + 1;
        const isSel = selectedCommentId === c.id;
        return (
          <div
            key={c.id}
            className={`anno-pin ${c.status === "resolved" ? "resolved" : ""}`}
            style={{
              left: `${c.annotation.x}%`, top: `${c.annotation.y}%`,
              outline: isSel ? "3px solid #fff" : undefined,
              boxShadow: isSel ? "0 0 0 4px hsl(var(--primary))" : undefined,
              pointerEvents: "auto",
            }}
            onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }}
            data-testid={`${testidPrefix}-pin-${c.id}`}
          >
            {num}
          </div>
        );
      })}
      {dragRect?.type === "rect" && (
        <div className="anno-rect pending absolute pointer-events-none"
          style={{ left: `${dragRect.x}%`, top: `${dragRect.y}%`, width: `${dragRect.w}%`, height: `${dragRect.h}%` }} />
      )}
      {pendingAnno?.type === "pin" && (
        <div className="anno-pin pending" style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%`, pointerEvents: "none" }} data-testid={`${testidPrefix}-pin-pending`}>+</div>
      )}
      {pendingAnno?.type === "rect" && (
        <div className="anno-rect pending absolute pointer-events-none"
          style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%`, width: `${pendingAnno.w}%`, height: `${pendingAnno.h}%` }} data-testid={`${testidPrefix}-rect-pending`} />
      )}

      {/* Callout bubble — when a pin/shape is selected, anchor a comment-bubble near it for quick reply/read */}
      {(() => {
        const sel = visibleComments.find((c) => c.id === selectedCommentId);
        if (!sel) return null;
        const a = sel.annotation;
        let left = a.x, top = a.y;
        if (a.type === "rect" || a.type === "circle") { left = a.x + (a.w || 0); top = a.y; }
        else if (a.type === "arrow") { left = a.end_x ?? a.x; top = a.end_y ?? a.y; }
        const clampLeft = Math.min(Math.max(left, 0), 70);
        const clampTop = Math.min(Math.max(top, 0), 92);
        return (
          <div
            className="absolute z-30 bg-card border border-primary shadow-xl p-3 max-w-[280px] text-xs"
            style={{ left: `${clampLeft}%`, top: `${clampTop}%`, transform: "translate(8px, -10px)", pointerEvents: "auto" }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testidPrefix}-callout-${sel.id}`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-medium">{sel.author_name}</div>
              <button onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(null); }} className="text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-callout-close`}>×</button>
            </div>
            <div className="text-[11px] mb-2 whitespace-pre-wrap max-h-32 overflow-auto">{sel.body}</div>
            <div className="text-[9px] text-muted-foreground">{sel.status === "resolved" ? "resolved" : "open"} · click panel to reply</div>
          </div>
        );
      })()}
    </>
  );

  // ----- URL bar form -----

  const [urlDraft, setUrlDraft] = useState(url || "");
  useEffect(() => { setUrlDraft(currentUrl); }, [currentUrl]);

  // ----- Render -----

  const isCapturing = !!capturingUrl;

  return (
    <div className={fullscreen ? "fixed inset-0 z-40 bg-background p-4 flex flex-col" : "flex flex-col"}>
      {/* Top bar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid={`${testidPrefix}-website-toolbar`}>
        <Button size="sm" variant="outline" onClick={goBack} disabled={historyIdx <= 0} className="rounded-none h-8 w-8 p-0" data-testid="website-back-btn"><ChevronLeft className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="outline" onClick={goFwd} disabled={historyIdx >= history.length - 1} className="rounded-none h-8 w-8 p-0" data-testid="website-fwd-btn"><ChevronRight className="w-3.5 h-3.5" /></Button>
        <Button size="sm" variant="outline" onClick={() => setIframeKey((k) => k + 1)} className="rounded-none h-8 w-8 p-0" data-testid="website-reload-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
        <div className="flex items-center gap-1 flex-1 min-w-[200px]">
          <Globe className="w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            value={urlDraft || ""}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") goToUrl(urlDraft); }}
            placeholder="https://"
            className="flex-1 bg-muted/40 border border-border h-8 px-2 text-xs font-mono outline-none focus:border-primary"
            data-testid="website-url-input"
          />
          <Button size="sm" variant="ghost" onClick={() => goToUrl(urlDraft)} className="rounded-none h-8 px-2 text-[10px]" data-testid="website-url-go-btn" title="Load URL">
            Go
          </Button>
        </div>
        <Button size="sm" onClick={enterBrowseMode} variant={mode === "browse" && !isInlineImage ? "default" : "outline"} className="rounded-none h-8" data-testid="website-mode-browse">
          <MousePointer2 className="w-3.5 h-3.5 mr-1.5" /> Browse
        </Button>
        <Button size="sm" onClick={enterAnnotateMode} variant={inAnnotateMode ? "default" : "outline"} disabled={isInlineImage || isCapturing} className="rounded-none h-8" data-testid="website-mode-annotate">
          {isCapturing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5 mr-1.5" />}
          {isCapturing ? "Capturing…" : "Annotate"}
        </Button>
        {inAnnotateMode && !isInlineImage && annotateImageSrc && (
          <Button size="sm" variant="outline" onClick={recaptureScreenshot} disabled={isCapturing} className="rounded-none h-8" data-testid="website-recapture-btn" title="Recapture this page's screenshot">
            <Camera className="w-3.5 h-3.5 mr-1.5" /> Recapture
          </Button>
        )}
        <a href={currentUrl} target="_blank" rel="noopener noreferrer">
          <Button size="sm" variant="outline" className="rounded-none h-8 w-8 p-0" title="Open in new tab" data-testid="website-extlink-btn"><ExternalLink className="w-3.5 h-3.5" /></Button>
        </a>
        {onToggleFullscreen && (
          <Button size="sm" variant="outline" onClick={onToggleFullscreen} className="rounded-none h-8 w-8 p-0" data-testid="website-fullscreen-btn" title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
        )}
      </div>

      {/* "Currently annotating" badge — makes it explicit which page pins/areas are being anchored to. */}
      {inAnnotateMode && !isInlineImage && effectiveAnnotatedUrl && (
        <div className="flex items-center gap-2 mb-2 text-[11px] flex-wrap" data-testid="website-annotating-badge">
          <span className="label-eyebrow">Annotating</span>
          <span className="font-mono bg-primary/10 border border-primary/30 px-2 py-0.5 truncate max-w-[60ch]">{effectiveAnnotatedUrl}</span>
          {urlDraft && urlDraft !== effectiveAnnotatedUrl && (
            <button
              type="button"
              onClick={() => goToUrl(urlDraft)}
              className="inline-flex items-center gap-1 text-amber-600 hover:underline"
              data-testid="website-annotate-newurl-btn"
              title="Switch to annotating the URL shown above"
            >
              ⚠ Switch to {urlDraft.replace(/^https?:\/\//, "").slice(0, 40)}…
            </button>
          )}
          <span className="ml-auto text-muted-foreground">
            {visibleComments.length} pin{visibleComments.length === 1 ? "" : "s"} on this page
          </span>
        </div>
      )}

      {/* Canvas: iframe (browse) OR scrollable image (annotate) */}
      {!inAnnotateMode ? (
        <div
          className="relative bg-white border border-border"
          style={{
            width: fullscreen ? "100%" : "min(100%, 1280px)",
            height: fullscreen ? "calc(100vh - 110px)" : undefined,
            aspectRatio: fullscreen ? undefined : "16 / 10",
            overflow: "hidden",
          }}
          data-testid={`${testidPrefix}-frame`}
        >
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={currentUrl}
            title="Website preview"
            className="absolute inset-0 w-full h-full"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            referrerPolicy="no-referrer"
            onLoad={handleIframeLoad}
            data-testid="website-iframe"
          />
        </div>
      ) : (
        <div
          ref={imgWrapRef}
          className="relative bg-white border border-border"
          style={{
            width: fullscreen ? "100%" : "min(100%, 1280px)",
            height: fullscreen ? "calc(100vh - 110px)" : "75vh",
            overflow: "auto",
          }}
          data-testid={`${testidPrefix}-frame`}
        >
          {!annotateImageSrc ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-3" data-testid="website-annotate-empty">
              {isCapturing ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <div className="text-sm font-mono">Capturing screenshot of {currentUrl}…</div>
                </>
              ) : (
                <>
                  <Camera className="w-6 h-6" />
                  <div className="text-sm">Click <strong>Annotate</strong> to capture this page for annotation.</div>
                </>
              )}
            </div>
          ) : (
            <div className="relative" data-testid="website-screenshot-content">
              <img
                src={annotateImageSrc}
                alt="Page screenshot for annotation"
                className="block w-full h-auto select-none pointer-events-none"
                draggable={false}
                data-testid="website-screenshot-img"
              />
              <div
                className={`absolute inset-0 ${overlayActive ? "cursor-crosshair" : ""}`}
                style={{ pointerEvents: overlayActive ? "auto" : "none" }}
                onClick={handleOverlayClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                data-testid={`${testidPrefix}-overlay`}
              >
                {renderPinsAndRects()}
              </div>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-2 px-1">
        <strong>Browse</strong> = live navigation (cross-origin pages can&apos;t be annotated directly).
        Click <strong>Annotate</strong> to capture this page as a screenshot you can pin precisely — pins anchor to the image and scroll with the content.
      </p>
    </div>
  );
}
